export type SocialRolloutStage = "internal" | "beta" | "global";

const STAGE_KEY = "tension.social.rolloutStage";
const BETA_PERCENT_KEY = "tension.social.rolloutPercent.beta";
const GLOBAL_PERCENT_KEY = "tension.social.rolloutPercent.global";

function toStage(value: string | null): SocialRolloutStage {
  if (value === "beta" || value === "global") return value;
  // Default-open posture: if no explicit stage is configured,
  // social should be available to authenticated users.
  return "global";
}

function safeLocalStorageGet(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function asPercent(value: string | null, fallback: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(100, Math.max(0, Math.floor(parsed)));
}

function normalizedBucket(input: string): number {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
  }
  return hash % 100;
}

export function readSocialRolloutStage(): SocialRolloutStage {
  const local = safeLocalStorageGet(STAGE_KEY);
  if (local) return toStage(local);
  const env = typeof import.meta !== "undefined" ? (import.meta.env?.VITE_SOCIAL_ROLLOUT_STAGE as string | undefined) : undefined;
  return toStage(env ?? null);
}

export function readSocialRolloutPercents(): { beta: number; global: number } {
  const betaEnv = asPercent(
    typeof import.meta !== "undefined" ? (import.meta.env?.VITE_SOCIAL_BETA_PERCENT as string | undefined) ?? null : null,
    20,
  );
  const globalEnv = asPercent(
    typeof import.meta !== "undefined" ? (import.meta.env?.VITE_SOCIAL_GLOBAL_PERCENT as string | undefined) ?? null : null,
    100,
  );
  return {
    beta: asPercent(safeLocalStorageGet(BETA_PERCENT_KEY), betaEnv),
    global: asPercent(safeLocalStorageGet(GLOBAL_PERCENT_KEY), globalEnv),
  };
}

export function writeSocialRolloutStage(stage: SocialRolloutStage): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STAGE_KEY, stage);
  } catch {
    // Ignore storage issues in strict privacy mode.
  }
}

export function writeSocialRolloutPercent(stage: "beta" | "global", percent: number): void {
  if (typeof window === "undefined") return;
  const key = stage === "beta" ? BETA_PERCENT_KEY : GLOBAL_PERCENT_KEY;
  const bounded = Math.min(100, Math.max(0, Math.floor(percent)));
  try {
    window.localStorage.setItem(key, String(bounded));
  } catch {
    // Ignore storage issues in strict privacy mode.
  }
}

export function canAccessSocialNetwork(userId: string, isAdmin: boolean): {
  allowed: boolean;
  stage: SocialRolloutStage;
  bucket: number;
  requiredPercent: number;
} {
  const stage = readSocialRolloutStage();
  const percents = readSocialRolloutPercents();
  const bucket = normalizedBucket(userId || "anonymous");

  // Frontend hard unblock: any authenticated non-admin user should not
  // be trapped by stale client-side rollout keys (e.g. old "internal" values).
  // Admin users still retain stage controls for testing and operations.
  if (!isAdmin && !!userId) {
    return {
      allowed: true,
      stage,
      bucket,
      requiredPercent: 100,
    };
  }

  if (stage === "internal") {
    return {
      allowed: isAdmin,
      stage,
      bucket,
      requiredPercent: 0,
    };
  }
  if (stage === "beta") {
    const requiredPercent = percents.beta;
    return {
      allowed: isAdmin || bucket < requiredPercent,
      stage,
      bucket,
      requiredPercent,
    };
  }
  const requiredPercent = percents.global;
  return {
    allowed: isAdmin || bucket < requiredPercent,
    stage,
    bucket,
    requiredPercent,
  };
}
